import { useCallback, useEffect, useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faAt,
  faBell,
  faCopy,
  faEnvelope,
  faMobileScreen,
  faPencil,
  faRotateRight,
  faTabletScreenButton,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

import notificationApi from '@/core/notifications/api/notification-api';
import {
  Contact,
  contactDisplayLabel,
  deviceLabelFor,
  NotificationChannel,
} from '@/core/notifications/model';
import { Loading, SectionHeader, AppModal } from '@/shared/components/export';
import NotificationRow, {
  NOTIFICATION_ROW_DIVIDER_STYLE,
} from '@/core/notifications/components/settings/notification-row';
import AddContactModal from '@/core/notifications/components/settings/add-contact-modal';
import EditContactLabelModal from '@/core/notifications/components/settings/edit-contact-label-modal';
import PushChannelRow, {
  SourcesData,
} from '@/core/notifications/components/settings/push-channel-row';

const CHANNEL_ICONS: Record<string, IconDefinition> = {
  'in-app': faBell,
  email: faEnvelope,
  sms: faMobileScreen,
  push: faTabletScreenButton,
};

function getChannelIcon(key: string) {
  return CHANNEL_ICONS[key] ?? faAt;
}

function ContactStatusPill({ isVerified, isDefault }: { isVerified: boolean; isDefault: boolean }) {
  return (
    <div className="d-flex align-items-center gap-1 flex-shrink-0">
      <span
        style={{
          fontSize: 12,
          borderRadius: 2,
          padding: '2px 6px',
          fontWeight: 600,
          background: isVerified
            ? 'rgba(var(--bs-success-rgb), 0.15)'
            : 'rgba(var(--bs-warning-rgb), 0.15)',
          color: isVerified ? 'var(--bs-success)' : 'var(--bs-warning)',
        }}
      >
        {isVerified ? 'Verified' : 'Pending'}
      </span>
      {isDefault && (
        <span
          style={{
            fontSize: 12,
            borderRadius: 2,
            padding: '2px 6px',
            fontWeight: 600,
            background: 'rgba(var(--bs-info-rgb), 0.15)',
            color: 'var(--bs-info)',
          }}
        >
          Default
        </span>
      )}
    </div>
  );
}

export default function ContactsSection({ channels }: { channels: NotificationChannel[] }) {
  const [contacts, setContacts] = useState<Contact[]>();
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const [contactToRename, setContactToRename] = useState<Contact | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [sourcesData, setSourcesData] = useState<SourcesData | null>(null);

  const loadSources = useCallback(() => {
    if (sourcesData) return;
    Promise.all([notificationApi.getSubscriptions(), notificationApi.getReceivedBroadcasts()])
      .then(([subscriptions, broadcasts]) => setSourcesData({ subscriptions, broadcasts }))
      .catch(() => toast.error('Could not load your alarms'));
  }, [sourcesData]);

  const loadContacts = useCallback(() => {
    return notificationApi
      .getContacts()
      .then(setContacts)
      .catch(() => {
        toast.error('Could not load contacts');
      });
  }, []);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const addableChannels = channels.filter(
    channel => channel.configFields.length > 0 && channel.key !== 'push',
  );

  const contactsByChannel = useMemo(() => {
    const map: Record<string, Contact[]> = {};
    (contacts ?? []).forEach(c => {
      (map[c.channelKey] ??= []).push(c);
    });
    return map;
  }, [contacts]);

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Copied to clipboard');
    } catch (e) {
      toast.error('Could not copy to clipboard');
    }
  };

  const handleSetDefault = async (contact: Contact) => {
    try {
      await notificationApi.setDefaultContact(contact.id);
      toast.success('Default contact updated');
      loadContacts();
    } catch (error: any) {
      toast.error(
        error?.response?.status === 422
          ? 'Contact is not yet verified'
          : 'Could not set the default contact',
      );
    }
  };

  const handleDelete = async () => {
    if (!contactToDelete) return;
    try {
      await notificationApi.deleteContact(contactToDelete.id);
      toast.success('Contact removed');
      loadContacts();
    } catch (e) {
      toast.error('Could not remove the contact');
    } finally {
      setContactToDelete(null);
    }
  };

  const actions = (
    <div className="d-flex align-items-center gap-2">
      <button
        className={`btn btn-sm btn-link p-0 mutedText`}
        onClick={loadContacts}
        aria-label="Refresh"
        title="Refresh"
      >
        <FontAwesomeIcon icon={faRotateRight} />
      </button>
      <button
        className="btn btn-sm btn-outline-secondary"
        disabled={addableChannels.length === 0}
        onClick={() => setShowAddModal(true)}
      >
        + New contact
      </button>
    </div>
  );

  if (!contacts)
    return (
      <div className="p-4">
        <Loading type="bar" />
      </div>
    );

  return (
    <div>
      <SectionHeader title="Contacts" />

      <div className={`px-3 pb-2 mutedText`} style={{ fontSize: 14 }}>
        Delivery addresses for each notification channel. New addresses must be verified before use.
      </div>

      <div className="px-3 pb-3 d-flex justify-content-end">{actions}</div>

      {channels.map(channel => {
        const isPush = channel.key === 'push';
        const hasFields = channel.configFields.length > 0;
        const channelContacts = contactsByChannel[channel.key] ?? [];

        return (
          <div
            key={channel.key}
            className="ms-3 mb-3"
            style={{
              border: '1px solid var(--bs-primary-200)',
              borderRadius: 0,
            }}
          >
            {/* Channel group header */}
            <div
              className="d-flex align-items-center gap-2 px-3 py-2"
              style={{ background: 'var(--bs-primary-200)' }}
            >
              <FontAwesomeIcon
                icon={getChannelIcon(channel.key)}
                className="mutedText"
                style={{ width: 13 }}
              />
              <span
                className="mutedText"
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                {channel.displayName}
              </span>
            </div>

            {/* Push: dedicated per-device toggle, not the generic add-contact flow */}
            {isPush && (
              <PushChannelRow
                contacts={channelContacts}
                onRequestDelete={setContactToDelete}
                onContactsChanged={loadContacts}
                sourcesData={sourcesData}
                loadSources={loadSources}
              />
            )}

            {/* Channels with no configurable fields (e.g. in-app): static automatic row */}
            {!isPush && !hasFields && (
              <NotificationRow style={NOTIFICATION_ROW_DIVIDER_STYLE}>
                <div className="d-flex align-items-center justify-content-between w-100">
                  <span style={{ fontSize: 14 }}>This app</span>
                  <span
                    style={{
                      fontSize: 12,
                      padding: '2px 8px',
                      borderRadius: 3,
                      fontWeight: 600,
                      background: 'rgba(var(--bs-success-rgb), 0.15)',
                      color: 'var(--bs-success)',
                      flexShrink: 0,
                    }}
                  >
                    Active — automatic
                  </span>
                </div>
              </NotificationRow>
            )}

            {/* Contact rows */}
            {!isPush &&
              channelContacts.map(contact => {
                const statusAndActions = (
                  <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    <ContactStatusPill
                      isVerified={contact.isVerified}
                      isDefault={contact.isDefault}
                    />
                    {!contact.isDefault && (
                      <OverlayTrigger
                        placement="left"
                        overlay={
                          !contact.isVerified ? <Tooltip>Verify this contact first</Tooltip> : <></>
                        }
                      >
                        <span>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            style={{ padding: '3px 8px', lineHeight: 1, fontSize: 12 }}
                            disabled={!contact.isVerified}
                            onClick={() => handleSetDefault(contact)}
                          >
                            Set default
                          </button>
                        </span>
                      </OverlayTrigger>
                    )}
                    {channel.supportsLabel && (
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        style={{ padding: '3px 8px', lineHeight: 1 }}
                        onClick={() => setContactToRename(contact)}
                        aria-label="Edit name"
                        title="Edit name"
                      >
                        <FontAwesomeIcon icon={faPencil} />
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      style={{ padding: '3px 8px', lineHeight: 1 }}
                      onClick={() => setContactToDelete(contact)}
                      aria-label="Delete"
                      title="Delete"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                );

                return (
                  <NotificationRow key={contact.id} style={NOTIFICATION_ROW_DIVIDER_STYLE}>
                    <div
                      className="w-100 d-flex flex-column flex-md-row align-items-md-center gap-1"
                      style={{ minWidth: 0 }}
                    >
                      {/* Line 1: address + actions (desktop) */}
                      <div
                        className="d-flex align-items-center gap-2 flex-grow-1"
                        style={{ minWidth: 0 }}
                      >
                        <div className="d-flex flex-column" style={{ minWidth: 0 }}>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip style={{ wordBreak: 'break-all' }}>
                                {contact.identityValue}
                              </Tooltip>
                            }
                          >
                            <span
                              className="text-truncate"
                              style={{
                                fontSize: 15,
                                maxWidth: 320,
                              }}
                            >
                              {contactDisplayLabel(contact)}
                            </span>
                          </OverlayTrigger>
                          {/* the label is already shown above, and the tooltip already
                              carries the raw address — repeating it here as a subtext
                              is what actually solves the "which webhook is this"
                              problem instead of just moving it into a tooltip.
                              Copy sits on this same line, at the URL's own height,
                              rather than centered between the label and the URL. */}
                          {contact.label && (
                            <div className="d-flex align-items-center gap-2">
                              <span
                                className="text-truncate mutedText"
                                style={{ fontSize: 12, maxWidth: 320 }}
                              >
                                {contact.identityValue}
                              </span>
                              <button
                                className="btn btn-sm btn-link p-0 text-muted flex-shrink-0"
                                onClick={() => handleCopy(contact.identityValue)}
                                aria-label="Copy"
                                title="Copy to clipboard"
                              >
                                <FontAwesomeIcon icon={faCopy} />
                              </button>
                            </div>
                          )}
                        </div>
                        {!contact.label && (
                          <button
                            className="btn btn-sm btn-link p-0 text-muted flex-shrink-0"
                            onClick={() => handleCopy(contact.identityValue)}
                            aria-label="Copy"
                            title="Copy to clipboard"
                          >
                            <FontAwesomeIcon icon={faCopy} />
                          </button>
                        )}
                        <div className="d-none d-md-flex ms-auto">{statusAndActions}</div>
                      </div>
                      {/* Line 2 (mobile only): status + actions */}
                      <div className="d-flex d-md-none align-items-center justify-content-end gap-2">
                        {statusAndActions}
                      </div>
                    </div>
                  </NotificationRow>
                );
              })}

            {/* Empty state for channels with configurable fields but no contacts */}
            {!isPush && hasFields && channelContacts.length === 0 && (
              <div className={`px-3 py-2 mutedText`} style={{ fontSize: 13 }}>
                No contacts yet.
              </div>
            )}
          </div>
        );
      })}

      <AppModal
        show={!!contactToDelete}
        onClose={() => setContactToDelete(null)}
        icon={faTrash}
        title="Confirm Removal"
        primaryLabel="Confirm Remove"
        onPrimary={handleDelete}
        primaryDanger
      >
        Are you sure you want to remove{' '}
        {/* Push contacts: name the device like its row does — identityValue is
            the raw endpoint URL, which is meaningless and too long to wrap. */}
        <strong style={{ overflowWrap: 'anywhere' }}>
          {contactToDelete &&
            (deviceLabelFor(contactToDelete) ?? contactDisplayLabel(contactToDelete))}
        </strong>
        ?
      </AppModal>

      {showAddModal && (
        <AddContactModal
          channels={addableChannels}
          onClose={() => setShowAddModal(false)}
          onAdded={loadContacts}
        />
      )}

      {contactToRename && (
        <EditContactLabelModal
          contact={contactToRename}
          onClose={() => setContactToRename(null)}
          onSaved={() => {
            setContactToRename(null);
            loadContacts();
          }}
        />
      )}
    </div>
  );
}
